import type { ReactNode } from 'react';
import type { Phase } from '../components/primitives/PhaseTrack';
import { addDays, demoAnchorMonday, fmtDowMonthDay } from './demoAnchor';

export type Race = {
  /** "Championship Final" */
  name: string;
  /** "Trail 10K" */
  distance: string;
  /** "Tue, May 20" */
  date: string;
  /** "Los Gatos" */
  location: string;
  /** 30 — rendered in big countdown numeral */
  daysAway: number;
  /** "Sub 58:00" — ghost target under countdown */
  goalTime: string;
  /** "current fit 59:40" */
  currentFit: string;
  /** For the phase track under the countdown. */
  phase: Phase;
  progressPct: number;
  /** Motivational coach line embedded inside the Championship card, after
   *  the phase track. Contains `<em>` for emphasized words. */
  motivationalLine: ReactNode;
};

/**
 * PR-G (DD-6/E5) — the demo race sits 30 days after the demo "today"
 * (the current week's Monday), so the countdown, the "30 days"
 * motivational line, and the weekday label stay correct forever. The
 * original static "Tue, May 20" fell into the past — with the wrong
 * weekday — as real time passed.
 */
export function demoChampionshipRace(now: Date = new Date()): Race {
  const raceDay = addDays(demoAnchorMonday(now), 30);
  return { ...championshipFinalStatic, date: fmtDowMonthDay(raceDay) };
}

const championshipFinalStatic: Race = {
  name: 'Championship Final',
  distance: 'Trail 10K',
  date: 'Tue, May 20', // overridden by demoChampionshipRace
  location: 'Los Gatos',
  daysAway: 30,
  goalTime: 'Sub 58:00',
  currentFit: 'current fit 59:40',
  phase: 'Build',
  progressPct: 38,
  motivationalLine: (
    <>
      &ldquo;30 days. <em>Four</em> long runs. Last Friday hurt because you{' '}
      <em>cared</em>. Race day wants that version of you.&rdquo;
    </>
  ),
};

export const championshipFinal: Race = demoChampionshipRace();

/**
 * Between-season state: plan exists, no race. Championship card shell
 * stays (removing it collapses layout) but content swaps to the no-race
 * copy from Decisions & Handoff §4c. Shipped as a dedicated variant in
 * step 9 — fixtures just expose `null` to signal the variant switch.
 */
export const noRace: Race | null = null;
